import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { pluck, switchMap } from 'rxjs/operators';
import { InfrastructureDto } from '../../../core/dto/infrastructure';
import { ContentService } from '../../../core/api/content.service';
import { NotificationService } from '../../../core/services/notification.service';
import { MetadataService } from '../../../core/services/metadata.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeleteConfirmationService } from '../../../common/delete-content-modal/delete-confirmation.service';

@UntilDestroy()
@Component({
    selector: 'isav-infrastructure-edit-page',
    templateUrl: './infrastructures-edit-page.html',
    styleUrls: ['./infrastructures-edit-page.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfrastructuresEditPage implements OnInit {
    data: InfrastructureDto;
    loading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private contentService: ContentService,
        private notificationService: NotificationService,
        private metaService: MetadataService,
        private deleteConfirmation: DeleteConfirmationService,
        private _cdRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.route.data.pipe(pluck('infrastructure'), untilDestroyed(this)).subscribe((data) => {
            this.data = data;
            this._cdRef.markForCheck();
        });
        this.metaService.setTitle('Edit Infrastructure');
    }

    onSave(data: Partial<InfrastructureDto>) {
        this.loading = true;
        this.contentService.patch<InfrastructureDto>(this.data['@id'], data).subscribe(
            (response) => {
                this.data = response;
                this.loading = false;
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully updated infrastructure',
                    type: 'success',
                    delay: 5000,
                });
                this._cdRef.markForCheck();
                this.router.navigate(['../view'], {
                    relativeTo: this.route,
                    queryParamsHandling: 'preserve',
                });
            },
            () => {
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }

    onDelete() {
        this.deleteConfirmation
            .confirmDeletion(this.data.name)
            .pipe(
                untilDestroyed(this),
                switchMap(() => {
                    return this.contentService.delete(this.data['@id']);
                })
            )
            .subscribe(() => {
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully deleted infrastructure',
                    type: 'success',
                    delay: 5000,
                });
                this.router.navigate(['/infrastructures']);
            });
    }

    publish(data: Partial<InfrastructureDto>) {
        this.loading = true;
        this.contentService.patchAndPublish<InfrastructureDto>(this.data['@id'], data).subscribe(
            () => {
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully published infrastructure',
                    type: 'success',
                    delay: 5000,
                });
                this.loading = false;
                this._cdRef.markForCheck();
                this.router.navigate(['../view'], {
                    relativeTo: this.route,
                    queryParamsHandling: 'preserve',
                });
            },
            (err) => {
                this.notificationService.add({
                    title: err['title'],
                    message: err['description'],
                    type: 'danger',
                    delay: 5000,
                });
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }

    unpublish() {
        this.loading = true;
        this.contentService.unpublish<InfrastructureDto>(this.data['@id']).subscribe(
            (data) => {
                this.data = data;
                this.loading = false;
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully unpublished infrastructure',
                    type: 'success',
                    delay: 5000,
                });
                this._cdRef.markForCheck();
            },
            () => {
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }
}
